import React from 'react';
import { RadialBarChart, RadialBar, PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Icon from '../../../components/AppIcon';

const COLORS = ['#f59e0b', '#a78bfa', '#22c55e', '#3b82f6', '#f97316'];

const PerformanceStats = ({ stats, categoryBreakdown, loading }) => {
  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl p-6 animate-pulse">
        <div className="h-5 bg-muted rounded w-40 mb-6" />
        <div className="grid grid-cols-2 gap-4">
          <div className="h-48 bg-muted/40 rounded-lg" />
          <div className="space-y-3">
            {[1, 2, 3, 4]?.map(i => <div key={i} className="h-10 bg-muted/40 rounded-lg" />)}
          </div>
        </div>
      </div>
    );
  }

  const winRate = stats?.winRate || 0;
  const radialData = [
    { name: 'Win Rate', value: winRate, fill: '#f59e0b' },
  ];

  const pieData = categoryBreakdown?.length
    ? categoryBreakdown
    : [
        { name: 'Sports Cards', value: 35 },
        { name: 'Comics', value: 25 },
        { name: 'Coins', value: 20 },
        { name: 'Stamps', value: 12 },
        { name: 'Other', value: 8 },
      ];

  const performanceMetrics = [
    {
      label: 'Win Rate',
      value: `${winRate?.toFixed(1)}%`,
      icon: 'Target',
      color: 'text-yellow-500',
      bg: 'bg-yellow-500/10',
      desc: `${stats?.auctionsWon || 0} wins / ${stats?.totalAuctions || 0} auctions`,
    },
    {
      label: 'Avg Bid per Auction',
      value: `$${(stats?.avgBidPerAuction || 0)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      icon: 'BarChart2',
      color: 'text-purple-500',
      bg: 'bg-purple-500/10',
      desc: `${stats?.totalBidsPlaced || 0} total bids placed`,
    },
    {
      label: 'Total Spent',
      value: `$${(stats?.totalSpent || 0)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
      icon: 'DollarSign',
      color: 'text-blue-500',
      bg: 'bg-blue-500/10',
      desc: `Avg $${(stats?.avgWinningBid || 0)?.toFixed(0)} per win`,
    },
    {
      label: 'Outbid Rate',
      value: `${(stats?.outbidRate || 0)?.toFixed(1)}%`,
      icon: 'AlertTriangle',
      color: 'text-red-400',
      bg: 'bg-red-500/10',
      desc: 'Times outbid vs. bids placed',
    },
  ];

  return (
    <div className="bg-card border border-border rounded-xl p-6">
      <h3 className="text-base font-semibold text-foreground mb-5">Performance Analytics</h3>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Win Rate Radial + Category Pie */}
        <div className="flex flex-col items-center gap-4">
          {/* Win Rate Gauge */}
          <div className="relative w-full flex flex-col items-center">
            <p className="text-xs text-muted-foreground mb-2">Win Rate</p>
            <div className="relative h-36 w-36">
              <ResponsiveContainer width="100%" height="100%">
                <RadialBarChart
                  cx="50%"
                  cy="50%"
                  innerRadius="65%"
                  outerRadius="90%"
                  startAngle={180}
                  endAngle={0}
                  data={radialData}
                >
                  <RadialBar
                    background={{ fill: 'var(--color-muted)' }}
                    dataKey="value"
                    cornerRadius={8}
                    fill="#f59e0b"
                  />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center mt-4">
                <span className="text-2xl font-bold text-foreground">{winRate?.toFixed(0)}%</span>
                <span className="text-xs text-muted-foreground">Win Rate</span>
              </div>
            </div>
          </div>

          {/* Category Breakdown Pie */}
          <div className="w-full">
            <p className="text-xs text-muted-foreground mb-2 text-center">Bids by Category</p>
            <ResponsiveContainer width="100%" height={160}>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={70}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieData?.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS?.[index % COLORS?.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '12px' }}
                  formatter={(value) => [`${value}%`, '']}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Metrics List */}
        <div className="space-y-3">
          {performanceMetrics?.map((metric, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${metric?.bg}`}>
                <Icon name={metric?.icon} size={16} className={metric?.color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-muted-foreground">{metric?.label}</p>
                <p className="text-sm font-bold text-foreground">{metric?.value}</p>
                <p className="text-xs text-muted-foreground/70 truncate">{metric?.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PerformanceStats;
